import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import api from '../services/api';
import type { WorkSchedule } from '../utils/jobSchedulePresentation';

export interface ScrapedJob {
  id: number;
  source: string;
  source_url: string | null;
  title: string;
  description: string;
  company: string;
  location: string | null;
  rate: string | null;
  rate_min: number | null;
  rate_max: number | null;
  rate_type: string | null;
  pay_provenance?: string | null;
  job_type: string;
  employment_type: string | null;
  roles_needed: string[];
  skills_required: string[];
  certifications_required: string[];
  union_required: string | boolean;
  start_date: string | null;
  end_date?: string | null;
  work_schedule?: WorkSchedule | null;
  apply_method: string;
  apply_contact: string | null;
  is_active: boolean;
  is_featured?: boolean;
  scraped_at: string;
  activity_status?: string | null;
  activity_checked_at?: string | null;
  created_at?: string;
  source_published_at?: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
}

export type ScrapedJobSort = 'recommended' | 'newest';

export interface MetroMarket {
  key: string;
  name: string;
  displayName: string;
  state: string;
  jobCount: number;
  industry: string;
  avgRate: { min: number; max: number; type: string };
}

export interface ScrapedJobsState {
  jobs: ScrapedJob[];
  job: ScrapedJob | null;
  markets: MetroMarket[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  marketsLoading: boolean;
  error: string | null;
  currentListRequestId: string | null;
  currentLoadMoreRequestId: string | null;
  currentJobRequestId: string | null;
  jobDetailStatus: 'idle' | 'loading' | 'found' | 'not_found' | 'error';
}

interface JobDetailFailure {
  kind: 'not_found' | 'error';
  message: string;
}

const initialState: ScrapedJobsState = {
  jobs: [],
  job: null,
  markets: [],
  loading: false,
  loadingMore: false,
  hasMore: false,
  marketsLoading: false,
  error: null,
  currentListRequestId: null,
  currentLoadMoreRequestId: null,
  currentJobRequestId: null,
  jobDetailStatus: 'idle',
};

export const fetchMarkets = createAsyncThunk(
  'scrapedJobs/fetchMarkets',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/scraped-jobs/markets');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch markets');
      }
      return rejectWithValue('Failed to fetch markets');
    }
  }
);

export const fetchScrapedJobs = createAsyncThunk(
  'scrapedJobs/fetchScrapedJobs',
  async (
    filters: {
      source?: string;
      jobType?: string;
      employmentType?: string;
      location?: string;
      metro?: string;
      role?: string;
      minRate?: number;
      search?: string;
      sort?: ScrapedJobSort;
      is_active?: boolean;
      limit?: number;
      offset?: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) params.append(key, String(value));
      });

      const response = await api.get(`/scraped-jobs?${params.toString()}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch scraped jobs');
      }
      return rejectWithValue('Failed to fetch scraped jobs');
    }
  }
);

export const loadMoreScrapedJobs = createAsyncThunk(
  'scrapedJobs/loadMoreScrapedJobs',
  async (
    filters: {
      source?: string;
      jobType?: string;
      employmentType?: string;
      location?: string;
      metro?: string;
      role?: string;
      minRate?: number;
      search?: string;
      sort?: ScrapedJobSort;
      is_active?: boolean;
      limit?: number;
      offset?: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) params.append(key, String(value));
      });

      const response = await api.get(`/scraped-jobs?${params.toString()}`);
      return { jobs: response.data, limit: filters.limit || 50 };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Failed to load more jobs');
      }
      return rejectWithValue('Failed to load more jobs');
    }
  }
);

export const fetchScrapedJobById = createAsyncThunk<
  ScrapedJob,
  number,
  { rejectValue: JobDetailFailure }
>(
  'scrapedJobs/fetchScrapedJobById',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await api.get(`/scraped-jobs/${id}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const notFound = error.response?.status === 404;
        return rejectWithValue({
          kind: notFound ? 'not_found' : 'error',
          message: notFound
            ? 'Job not found'
            : (error.response?.data?.message || 'Failed to fetch scraped job'),
        });
      }
      return rejectWithValue({ kind: 'error', message: 'Failed to fetch scraped job' });
    }
  }
);

const scrapedJobsSlice = createSlice({
  name: 'scrapedJobs',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchScrapedJobs.pending, (state, action) => {
        state.loading = true;
        state.loadingMore = false;
        state.error = null;
        state.currentListRequestId = action.meta.requestId;
        state.currentLoadMoreRequestId = null;
      })
      .addCase(fetchScrapedJobs.fulfilled, (state, action) => {
        if (state.currentListRequestId !== action.meta.requestId) return;

        state.loading = false;
        state.jobs = action.payload;
        state.hasMore = action.payload.length >= 50;
        state.currentListRequestId = null;
      })
      .addCase(fetchScrapedJobs.rejected, (state, action) => {
        if (state.currentListRequestId !== action.meta.requestId) return;

        state.loading = false;
        state.error = action.payload as string;
        state.currentListRequestId = null;
      })
      .addCase(loadMoreScrapedJobs.pending, (state, action) => {
        state.loadingMore = true;
        state.currentLoadMoreRequestId = action.meta.requestId;
      })
      .addCase(loadMoreScrapedJobs.fulfilled, (state, action) => {
        if (state.currentLoadMoreRequestId !== action.meta.requestId) return;

        state.loadingMore = false;
        state.jobs = [...state.jobs, ...action.payload.jobs];
        state.hasMore = action.payload.jobs.length >= action.payload.limit;
        state.currentLoadMoreRequestId = null;
      })
      .addCase(loadMoreScrapedJobs.rejected, (state, action) => {
        if (state.currentLoadMoreRequestId !== action.meta.requestId) return;

        state.loadingMore = false;
        state.error = action.payload as string;
        state.currentLoadMoreRequestId = null;
      })
      .addCase(fetchScrapedJobById.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.job = null;
        state.currentJobRequestId = action.meta.requestId;
        state.jobDetailStatus = 'loading';
      })
      .addCase(fetchScrapedJobById.fulfilled, (state, action) => {
        if (state.currentJobRequestId !== action.meta.requestId) return;

        state.loading = false;
        state.job = action.payload;
        state.currentJobRequestId = null;
        state.jobDetailStatus = 'found';
      })
      .addCase(fetchScrapedJobById.rejected, (state, action) => {
        if (state.currentJobRequestId !== action.meta.requestId) return;

        state.loading = false;
        state.job = null;
        state.error = action.payload?.message || 'Failed to fetch scraped job';
        state.currentJobRequestId = null;
        state.jobDetailStatus = action.payload?.kind === 'not_found' ? 'not_found' : 'error';
      })
      .addCase(fetchMarkets.pending, (state) => {
        state.marketsLoading = true;
      })
      .addCase(fetchMarkets.fulfilled, (state, action) => {
        state.marketsLoading = false;
        state.markets = action.payload;
      })
      .addCase(fetchMarkets.rejected, (state, action) => {
        state.marketsLoading = false;
        state.error = action.payload as string || 'Failed to load markets';
      });
  },
});

export default scrapedJobsSlice.reducer;
